// DivergingBarChart — the net_flow card's signature daily view: an up-bar for
// premium takers PAID (buy, blue) and a down-bar for premium takers RECEIVED
// (sell, red), with a dot at the day's net. Bespoke SVG (~a screenful) because
// the shared chart wrapper is single-series — a true dual up/down bar isn't a
// single histogram. Ported from reference_poc.html `dailyChart`, re-tokened.
//
// Colours resolve to hex via getTokenValue (SVG presentation attributes don't
// read CSS variables), so the chart re-derives them on theme change.

import { useThemeMode } from '../../../../providers/theme-provider';
import { getTokenValue } from '../../../../styles/tokens';
import { fmtUsd } from '../../../../providers/options-flow/options-flow.helpers';
import { NetFlowDailyPoint } from '../../../../providers/options-flow/types';

interface DivergingBarChartProps {
  daily: NetFlowDailyPoint[];
  height?: number;
}

const W = 1080;
const PAD_L = 64;
const PAD_R = 16;
const PAD_T = 14;
const PAD_B = 26;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export const DivergingBarChart = ({ daily, height = 200 }: DivergingBarChartProps) => {
  const { resolvedMode } = useThemeMode();

  const buy = getTokenValue('flow.buy', resolvedMode);
  const sell = getTokenValue('flow.sell', resolvedMode);
  const grid = getTokenValue('border.default', resolvedMode);
  const axis = getTokenValue('border.subtle', resolvedMode);
  const muted = getTokenValue('text.muted', resolvedMode);
  const dotFill = getTokenValue('bg.surface', resolvedMode);

  const H = height;

  // Symmetric value axis around zero, scaled to the largest single-side
  // magnitude (buy up, |sell| down) across the window.
  const mx = Math.max(1, ...daily.map((d) => Math.max(d.buy, -d.sell)));
  const yOf = (v: number) => lerp(H - PAD_B, PAD_T, (v + mx) / (2 * mx));
  const yZero = yOf(0);

  const gridLines = [0, 1, 2, 3, 4];
  const barW = Math.min(24, ((W - PAD_L - PAD_R) / Math.max(1, daily.length)) * 0.6);

  const xOf = (i: number) =>
    lerp(PAD_L + barW, W - PAD_R - barW, daily.length < 2 ? 0 : i / (daily.length - 1));

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      role="img"
      aria-label="Daily bought versus sold premium"
    >
      {gridLines.map((g) => {
        const gy = lerp(PAD_T, H - PAD_B, g / 4);
        return (
          <g key={`grid-${g}`}>
            <line x1={PAD_L} y1={gy} x2={W - PAD_R} y2={gy} stroke={grid} strokeWidth={1} />
            <text x={PAD_L - 8} y={gy + 3} fill={muted} fontSize={10} textAnchor="end">
              {fmtUsd(lerp(mx, -mx, g / 4))}
            </text>
          </g>
        );
      })}

      <line x1={PAD_L} y1={yZero} x2={W - PAD_R} y2={yZero} stroke={axis} strokeWidth={1} />

      {daily.map((d, i) => {
        const cx = xOf(i);
        const buyTop = yOf(d.buy);
        const sellBottom = yOf(d.sell);
        return (
          <g key={d.date}>
            <rect
              x={cx - barW / 2}
              y={buyTop}
              width={barW}
              height={Math.abs(buyTop - yZero)}
              rx={3}
              fill={buy}
              opacity={0.9}
            />
            <rect
              x={cx - barW / 2}
              y={yZero}
              width={barW}
              height={Math.abs(sellBottom - yZero)}
              rx={3}
              fill={sell}
              opacity={0.9}
            />
            <circle
              cx={cx}
              cy={yOf(d.net)}
              r={3}
              fill={dotFill}
              stroke={d.net >= 0 ? buy : sell}
              strokeWidth={1.5}
            />
            <text x={cx} y={H - 8} fill={muted} fontSize={9} textAnchor="middle">
              {d.date.slice(5)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
